/**
 * What the server refused this session anyway (API_ACCESS.md §3.6; decided 2026-10-06 that it
 * lives here, as a session fact the engine reads, and not in the client): a route answered with a
 * 403 the frontend did not predict is suspended for the subject it was refused to. The client
 * (`@rista/core/api`) writes it and reports; the engine and the client's `canCall` read it.
 *
 * A suspension is held against the subject's facts as an identity, so new facts (a new user
 * record, a licence switch) lift it by themselves: nothing has to clear it, and no read changes
 * state. The subject source memoises its facts on the user record (`sources/store.ts`).
 */
import type { SubjectFacts } from "./types";

export interface Refusals {
  isSuspended(key: string, subject: SubjectFacts): boolean;
  /** true when the route was not already suspended for this subject */
  suspend(key: string, subject: SubjectFacts): boolean;
  subscribe(listener: () => void): () => void;
}

export function createRefusals(): Refusals {
  const suspended = new Map<string, SubjectFacts>();
  const listeners = new Set<() => void>();
  return {
    isSuspended: (key, subject) => suspended.get(key) === subject,
    suspend(key, subject) {
      if (suspended.get(key) === subject) return false;
      suspended.set(key, subject);
      listeners.forEach((listener) => listener());
      return true;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
